import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import DashboardLayout from "../dashboard/DashboardLayout";
import { 
  MapPin, 
  Link2, 
  Calendar, 
  FileText, 
  Building2, 
  Briefcase, 
  ExternalLink, 
  Eye, 
  Trash2, 
  X, 
  RefreshCw, 
  List, 
  LayoutGrid, 
  Plus,
  Zap,
  MoreVertical,
  CheckCircle2,
  Clock,
  AlertCircle,
  Pencil,
  Save
} from "lucide-react";
import { trackPageView, trackButtonClick, trackEvent } from "../../utils/analytics";

const emptyJob = { company: "", title: "", location: "", source: "", url: "", status: "saved", notes: "", jd_text: "", next_action_date: "" };

const StatusBadge = ({ s }) => {
  const styles = { 
    saved: "bg-slate-100 text-slate-700 border-slate-200", 
    applied: "bg-blue-100 text-blue-700 border-blue-200", 
    interview: "bg-amber-100 text-amber-700 border-amber-200", 
    offer: "bg-emerald-100 text-emerald-700 border-emerald-200", 
    rejected: "bg-rose-100 text-rose-700 border-rose-200" 
  };
  const defaultStyle = "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${styles[s] || defaultStyle} shadow-sm`}>
      {s}
    </span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
};

const JobTracker = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [draft, setDraft] = useState({ ...emptyJob });
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(() => {
    // Default to list view on mobile devices (width < 768px)
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768 ? "list" : "board";
    }
    return "board";
  }); // board | list
  const [dragOverCol, setDragOverCol] = useState(null);
  const [draggingJobId, setDraggingJobId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const openJob = (j) => {
    setSelectedJob(j);
    setEditForm(j);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    try {
      await update(selectedJob.id, editForm);
      setSelectedJob({ ...selectedJob, ...editForm });
      setIsEditing(false);
    } catch (e) {
      setErr("Failed to save changes");
    }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try { const r = await axiosInstance.get("/jobs"); setJobs(r.data || []); } catch (e) { setErr("Failed to load pipeline data"); }
    setLoading(false);
  };
  useEffect(() => { 
    trackPageView("/job-tracker", "Job Tracker");
    load(); 
  }, []);

  const create = async () => {
    if (!draft.company || !draft.title) {
        setErr("Company and Title are required");
        return;
    }
    setErr("");
    trackButtonClick("Add Job", "Job Tracker");
    try { await axiosInstance.post("/jobs", draft); setDraft({ ...emptyJob }); load(); } catch (e) { setErr("Failed to add entry"); }
  };
  const update = async (id, patch) => {
    if (patch.status) trackEvent("job_status_change", "Interaction", patch.status);
    try { await axiosInstance.patch(`/jobs/${id}`, patch); load(); } catch {}
  };
  const remove = async (id) => { if (!window.confirm("Delete job from pipeline?")) return; try { await axiosInstance.delete(`/jobs/${id}`); load(); } catch {} };
  const genCover = async (job) => {
    trackButtonClick("Generate Cover Letter", "Job Tracker");
    try {
      const resp = await axiosInstance.post("/cover_letters/generate", { company: job.company, title: job.title, job_url: job.url, save: true }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `cover_${job.company || 'letter'}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr("Failed to generate cover letter"); }
  };

  const list = jobs.filter(j => filter === "all" ? true : j.status === filter);
  const statuses = ["saved", "applied", "interview", "offer", "rejected"];

  const onCardDragStart = (e, id) => {
    setDraggingJobId(id);
    e.dataTransfer.setData('text/plain', id);
  };
  const onColDrop = async (e, s) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    setDragOverCol(null);
    setDraggingJobId(null);
    if (id) {
      setJobs(prev => prev.map(j => j.id === id ? { ...j, status: s } : j));
      await update(id, { status: s });
    }
  };

  return (
    <DashboardLayout activeTab="job-tracker" setTab={(t) => navigate('/dashboard', { state: { tab: t } })}>
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
           <div className="flex flex-col">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight italic flex items-center gap-3">
                 <Briefcase size={28} className="text-blue-500" /> Job Pipeline
              </h2>
              <div className="flex items-center gap-2 mt-1">
                 <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{jobs.length} Active Opportunities</span>
              </div>
           </div>

           <div className="flex items-center gap-3 bg-white p-1 rounded-2xl border border-slate-200 shadow-sm">
              <button 
                onClick={() => setView('board')}
                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  view === 'board' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <LayoutGrid size={14} /> Board
              </button>
              <button 
                onClick={() => setView('list')}
                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  view === 'list' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <List size={14} /> List
              </button>
              <div className="w-px h-6 bg-slate-100 mx-1" />
              <button onClick={load} className="p-2 text-slate-400 hover:text-blue-600 transition-colors"><RefreshCw size={18} /></button>
           </div>
        </div>

        {err && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-700 font-bold animate-in slide-in-from-top-2">
             <AlertCircle size={18} /> <span className="text-sm">{err}</span>
          </div>
        )}

        {/* Global Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 pb-2 overflow-x-auto scrollbar-none">
           <button 
             onClick={() => setFilter('all')}
             className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border ${
               filter === 'all' ? 'bg-blue-600 text-white border-blue-600 shadow-xl shadow-blue-100' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
             }`}
           >
             All Active
           </button>
           {statuses.map(s => (
             <button
               key={s}
               onClick={() => setFilter(s)}
               className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border ${
                 filter === s ? 'bg-slate-900 text-white border-slate-900 shadow-xl shadow-slate-200' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
               }`}
             >
               {s}
             </button>
           ))}
        </div>

        {/* Add Entry Card */}
        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm group hover:border-blue-200 transition-all overflow-hidden relative">
           <div className="absolute top-0 right-0 p-8 opacity-5 -rotate-12 pointer-events-none group-hover:rotate-0 transition-transform duration-1000">
              <Plus size={120} />
           </div>
           
           <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
              <Zap size={16} className="text-blue-500" /> Inject New Opportunity
           </h3>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="space-y-1">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Company</label>
                 <input className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all border-b-2" placeholder="e.g., SpaceX" value={draft.company} onChange={(e) => setDraft({ ...draft, company: e.target.value })} />
              </div>
              <div className="space-y-1">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Title</label>
                 <input className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all border-b-2" placeholder="e.g., Principal OS Engineer" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </div>
              <div className="space-y-1">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Location</label>
                 <input className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all border-b-2" placeholder="Remote / Mars" value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
              </div>
              <div className="space-y-1">
                 <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Pipeline Status</label>
                 <select className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all border-b-2 cursor-pointer" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                    {statuses.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                 </select>
              </div>
           </div>
           
           <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <p className="text-[10px] font-bold text-slate-400 italic">Complete all fields for better tracking & AI generation performance.</p>
              <button 
                className="px-8 py-3 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all active:scale-95 flex items-center gap-2 italic"
                onClick={create}
              >
                <Plus size={16} className="stroke-[3]" /> Create Entry
              </button>
           </div>
        </div>

        {view === 'board' ? (
          <div className="flex gap-6 overflow-x-auto pb-8 snap-x min-h-[600px] scrollbar-thin">
            {statuses.map((s) => (
              <div 
                key={s} 
                className={`flex-1 min-w-[320px] flex flex-col bg-slate-50/40 rounded-[2.5rem] border-2 border-dashed transition-all snap-start ${
                  dragOverCol === s ? 'bg-blue-50/50 border-blue-400 ring-4 ring-blue-50' : 'border-slate-100'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOverCol(s); }}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={(e) => onColDrop(e, s)}
              >
                <div className="p-6 flex justify-between items-center group/title">
                   <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${s==='offer' ? 'bg-emerald-500' : s==='rejected' ? 'bg-rose-500' : 'bg-blue-500'}`} />
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-[0.2em] italic">{s}</h4>
                   </div>
                   <div className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-slate-400 group-hover/title:text-blue-600 group-hover/title:border-blue-200 transition-all">
                      {jobs.filter(j => j.status === s).length}
                   </div>
                </div>

                <div className="flex-1 p-4 space-y-4">
                  {jobs.filter(j => j.status === s && (filter === 'all' || filter === s)).map((j) => (
                    <div 
                      key={j.id} 
                      className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all duration-300 group cursor-grab active:cursor-grabbing relative overflow-hidden"
                      draggable 
                      onDragStart={(e) => onCardDragStart(e, j.id)}
                      onClick={() => openJob(j)}
                    >
                      <div className="flex items-start justify-between mb-4">
                         <div className="p-3 bg-slate-50 rounded-2xl text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                            <Building2 size={20} />
                         </div>
                         <button className="p-2 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); remove(j.id); }}><Trash2 size={16}/></button>
                      </div>
                      
                      <div className="space-y-1 mb-6">
                         <h5 className="font-black text-slate-900 text-sm italic line-clamp-2 leading-snug">{j.title || "Untitled Role"}</h5>
                         <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 truncate">
                            {j.company} <span>•</span> {j.location || "Global"}
                         </p>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                         <div className="flex -space-x-2">
                             <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-black text-slate-400" title="Source: Web"><Link2 size={12}/></div>
                             <div className="w-8 h-8 rounded-full bg-blue-50 border-2 border-white flex items-center justify-center text-[10px] font-black text-blue-500" title="AI Ready"><Zap size={12} className="fill-blue-500"/></div>
                         </div>
                         <button className="p-2 bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-900 hover:text-white transition-all" onClick={(e) => { e.stopPropagation(); openJob(j); }}><Eye size={16}/></button>
                      </div>
                    </div>
                  ))}
                  {jobs.filter(j => j.status === s).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 opacity-20 select-none pointer-events-none">
                       <LayoutGrid size={40} className="mb-2" />
                       <span className="text-[10px] font-black uppercase tracking-widest">Zone Clear</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-[2.5rem] shadow-sm overflow-hidden">
             <div className="divide-y divide-slate-100">
                {list.length > 0 ? list.map(j => (
                  <div key={j.id} className="p-6 hover:bg-slate-50 transition-all flex items-center justify-between group">
                     <div className="flex items-center gap-6">
                        <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 font-black italic group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                           {j.company.slice(0,1)}
                        </div>
                        <div>
                           <div className="text-sm font-black text-slate-900 italic tracking-tight">{j.title}</div>
                           <div className="flex items-center gap-3 mt-1">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{j.company}</span>
                              <div className="w-1 h-1 rounded-full bg-slate-200" />
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1"><MapPin size={10}/> {j.location || 'Remote'}</span>
                           </div>
                        </div>
                     </div>
                     <div className="flex items-center gap-6">
                        <StatusBadge s={j.status} />
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0">
                           <button className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-blue-600 hover:border-blue-200 shadow-sm transition-all" onClick={() => openJob(j)}><Eye size={16}/></button>
                           <button className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600 hover:border-indigo-200 shadow-sm transition-all" onClick={() => genCover(j)}><FileText size={16}/></button>
                           <button className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-red-500 hover:border-red-200 shadow-sm transition-all" onClick={() => remove(j.id)}><Trash2 size={16}/></button>
                        </div>
                     </div>
                  </div>
                )) : (
                   <div className="py-32 text-center">
                      <Zap size={48} className="mx-auto text-slate-100 mb-4" />
                      <p className="text-xs font-black text-slate-300 uppercase tracking-[0.2em] italic">Pipeline Synchronized / 0 Active Entries</p>
                   </div>
                )}
             </div>
          </div>
        )}

        {/* Dynamic Detail Modal */}
        {selectedJob && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setSelectedJob(null)} />
            <div className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-400 border border-slate-100">
               
               {/* Compact Header */}
               <div className="bg-slate-900 p-5 text-white flex justify-between items-center relative shrink-0">
                  <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none" />
                  <div className="relative z-10 flex items-center gap-4">
                     <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-xl font-black italic shadow-lg shadow-blue-500/20">
                        {selectedJob.company.slice(0, 1)}
                     </div>
                     <div>
                        {isEditing ? (
                           <input 
                              className="bg-transparent border-b border-blue-500/50 text-xl font-black italic tracking-tighter leading-none text-white focus:outline-none focus:border-blue-500 w-full"
                              value={editForm.title}
                              onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                              placeholder="Job Title"
                           />
                        ) : (
                           <h2 className="text-xl font-black italic tracking-tighter leading-none">{selectedJob.title}</h2>
                        )}
                        <div className="text-blue-400 text-[10px] font-black uppercase tracking-widest mt-1">
                           {isEditing ? (
                              <input 
                                 className="bg-transparent border-b border-blue-500/50 text-[10px] font-black uppercase tracking-widest text-blue-400 focus:outline-none focus:border-blue-500 mt-1"
                                 value={editForm.company}
                                 onChange={(e) => setEditForm({...editForm, company: e.target.value})}
                                 placeholder="Company"
                              />
                           ) : selectedJob.company}
                        </div>
                     </div>
                  </div>
                  <div className="relative z-10 flex items-center gap-2">
                     {!isEditing && (
                        <button onClick={() => setIsEditing(true)} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all text-white/70 hover:text-white border border-white/5" title="Edit Objective">
                           <Pencil size={16} />
                        </button>
                     )}
                     <button onClick={() => setSelectedJob(null)} className="p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all text-white/50 hover:text-white border border-white/10"><X size={18}/></button>
                  </div>
               </div>

               <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {isEditing ? (
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-in slide-in-from-bottom-2">
                        <div className="space-y-1">
                           <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Location</label>
                           <input className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                           <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Status</label>
                           <select className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                              {statuses.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                           </select>
                        </div>
                        <div className="space-y-1">
                           <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Next Action Date</label>
                           <input type="date" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all" value={editForm.next_action_date ? new Date(editForm.next_action_date).toISOString().split('T')[0] : ''} onChange={(e) => setEditForm({ ...editForm, next_action_date: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                           <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Link</label>
                           <input className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all" value={editForm.url || ''} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} placeholder="https://..." />
                        </div>
                        <div className="space-y-1 md:col-span-2">
                           <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Intelligence / Notes</label>
                           <textarea className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium font-sans outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 transition-all min-h-[200px]" value={editForm.notes || editForm.jd_text || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Paste JD or notes here..." />
                        </div>
                     </div>
                  ) : (
                     <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                           <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                              <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5 italic"><MapPin size={10}/> Location</div>
                              <div className="text-xs font-black text-slate-800 italic uppercase truncate">{selectedJob.location || 'Remote'}</div>
                           </div>
                           <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                              <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5 italic"><Clock size={10}/> Phase</div>
                              <StatusBadge s={selectedJob.status} />
                           </div>
                           <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                              <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5 italic"><Calendar size={10}/> Next Action</div>
                              <div className="text-xs font-black text-slate-800 italic text-amber-600">{selectedJob.next_action_date ? formatDate(selectedJob.next_action_date) : 'Pending Intel'}</div>
                           </div>
                           <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                              <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5 italic"><Link2 size={10}/> Source</div>
                              {selectedJob.url ? (
                                 <a href={selectedJob.url} target="_blank" rel="noreferrer" className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline italic truncate block">View Feed</a>
                              ) : <span className="text-[10px] text-slate-300 font-bold italic">N/A</span>}
                           </div>
                        </div>

                        <div className="space-y-4">
                           <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest italic flex items-center gap-2 border-b border-slate-100 pb-2"><FileText size={14} className="text-blue-500" /> Intelligence Report</h4>
                           <div className="bg-slate-50 rounded-2xl p-6 relative overflow-hidden group/jd shadow-inner border border-slate-100">
                              <pre className="text-xs font-medium font-sans text-slate-600 whitespace-pre-wrap leading-relaxed italic pr-2 max-h-[300px] overflow-auto scrollbar-thin">
                                 {selectedJob.jd_text || selectedJob.notes || "No additional intelligence available for this objective."}
                              </pre>
                           </div>
                        </div>
                     </>
                  )}
               </div>

               <div className="p-6 bg-slate-50/80 border-t border-slate-100 flex flex-wrap gap-3 shrink-0 shadow-lg">
                  {isEditing ? (
                     <>
                        <button className="px-6 py-3 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all flex items-center gap-2 italic" onClick={handleSaveEdit}>
                           <Save size={16} /> Save Intel
                        </button>
                        <button className="px-6 py-3 bg-white border border-slate-200 text-slate-500 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all italic" onClick={() => setIsEditing(false)}>
                           Cancel Operation
                        </button>
                     </>
                  ) : (
                     <>
                        <button className="px-6 py-3 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all flex items-center gap-2 italic" onClick={() => genCover(selectedJob)}>
                           <Zap size={16} className="fill-white"/> Gen Cover Letter
                        </button>
                        <button className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black shadow-lg shadow-slate-200 transition-all italic flex items-center gap-2">
                           <RefreshCw size={16}/> Update Intel
                        </button>
                        <button className="px-6 py-3 bg-white border-2 border-rose-100 text-rose-500 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-rose-50 transition-all ml-auto italic" onClick={() => { remove(selectedJob.id); setSelectedJob(null); }}>
                           Purge Objective
                        </button>
                     </>
                  )}
               </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default JobTracker;
